import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import {
  CALL_INVIS_CONCERNS,
  CALL_INVIS_DECIDES,
  CALL_INVIS_EVENTS,
  CALL_INVIS_SMILE,
  CALL_REACH_OPTIONS,
  CALL_REASON_META,
  callOutcomesFor,
} from '../../calls/mockData';
import type { CallRow } from '../../calls/types';

export interface LogCallModalProps {
  open: boolean;
  row: CallRow | null;
  /** Who is logging + the call window, e.g. "Maria, 10:42 – 10:49 am". */
  by: string;
  onClose: () => void;
  onSave: (row: CallRow) => void;
  onStopCalls: (row: CallRow) => void;
}

/**
 * Log the call (spec 31 §5, board CL3LogCall). Question 2 (per-reason outcomes)
 * appears only when *Reached*; Save needs Q1 and, when reached, every reason
 * picked. A green *What happens next* box tracks the picks; the red link opens
 * the *Don't call again* confirm.
 */
export function LogCallModal({ open, row, by, onClose, onSave, onStopCalls }: LogCallModalProps) {
  const [reach, setReach] = useState<string | null>(null);
  const [picks, setPicks] = useState<Record<number, string>>({});
  const [note, setNote] = useState('');
  const [confirmStop, setConfirmStop] = useState(false);

  React.useEffect(() => {
    if (open) { setReach(null); setPicks({}); setNote(''); setConfirmStop(false); }
  }, [open, row?.id]);

  if (!open || !row) return null;

  const reached = reach === 'reached';
  const allPicked = !reached || row.reasons.every((_, i) => picks[i] !== undefined);
  const canSave = reach !== null && allPicked;

  const setPick = (i: number, key: string) => { setPicks((p) => ({ ...p, [i]: key })); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(20,22,28,0.42)] px-4 py-8" role="dialog" aria-modal="true" aria-labelledby="logcall-title">
      <div className="flex max-h-full w-full max-w-[560px] flex-col overflow-hidden rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-modal)]">
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-5 py-4">
          <div>
            <h2 id="logcall-title" className="m-0 text-[16px] font-bold text-[var(--text-primary)]">Log the call</h2>
            <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-secondary)]">{row.name} · {row.phone} · {by}</p>
          </div>
          <button type="button" aria-label="Close" className="text-[20px] leading-none text-[var(--text-muted)] hover:text-[var(--text-primary)]" onClick={onClose}>×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Q1 */}
          <Step n="1" title={`Did you reach ${firstName(row.name)}?`} />
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {CALL_REACH_OPTIONS.map((o) => (
              <button key={o.key} type="button" onClick={() => { setReach(o.key); }}
                className={cn('rounded-[10px] border px-3 py-2.5 text-left text-[13px] font-semibold',
                  o.key === reach ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]' : 'border-[var(--border-default)] hover:bg-[var(--surface-sunken)]',
                  o.red ? 'text-[var(--alert-red)]' : 'text-[var(--text-primary)]')}>
                {o.label}
              </button>
            ))}
          </div>

          {/* Q2 — only when reached */}
          {reached ? (
            <>
              <Step n="2" title="How did each reason go?" note="one tap each" className="mt-5" />
              <div className="mt-2 flex flex-col gap-2.5">
                {row.reasons.map((r, i) => {
                  const meta = CALL_REASON_META[r.type];
                  const opts = callOutcomesFor(r.type);
                  const picked = picks[i];
                  return (
                    <div key={`${r.type}-${String(i)}`} className="rounded-[12px] border border-[var(--border-default)] p-3">
                      <div className="flex items-baseline gap-2">
                        <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: meta.fg, background: meta.bg }}>{meta.label}</span>
                        <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{r.title}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {opts.map((o) => (
                          <button key={o.key} type="button" onClick={() => { setPick(i, o.key); }}
                            className={cn('rounded-full border px-3 py-1 text-[12px] font-semibold',
                              o.key === picked ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
                              o.red && o.key !== picked ? 'text-[var(--alert-red)]' : '')}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                      {picked === 'booked' ? (
                        <div className="mt-2 rounded-[8px] bg-[var(--money-green-fill)] px-3 py-1.5 text-[11.5px] text-[#1b5f45]">
                          Booked 14 Oct, 8:00 am with Dr. Carter — found in the calendar
                        </div>
                      ) : null}
                      {r.type === 'invisalign' && picked !== undefined && picked !== 'not-interested' ? (
                        <InvisFold />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="mt-3 text-[12px] text-[var(--text-muted)]">Question 2 appears only when you reach them.</p>
          )}

          {/* Q3 */}
          <Step n="3" title="A quick note" note="optional · stays inside the practice, never sent to the patient" className="mt-5" />
          <textarea
            value={note} maxLength={200} onChange={(e) => { setNote(e.target.value); }}
            className="mt-2 h-[68px] w-full resize-none rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
            placeholder="What you want the next person to know…"
          />
          <div className="mt-1 text-right text-[11px] text-[var(--text-muted)]">{note.length} / 200</div>

          {/* what happens next */}
          <div className="mt-3 rounded-[10px] border border-[var(--money-green-line)] bg-[var(--money-green-fill)] px-3.5 py-2.5">
            <div className="text-[12px] font-bold text-[#1b5f45]">What happens next</div>
            <div className="mt-0.5 text-[12px] leading-[1.55] text-[var(--text-secondary)]">{whatHappensNext(row, reach, picks, by)}</div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[var(--border-default)] px-5 py-3">
          <button type="button" className="text-[12px] font-semibold text-[var(--alert-red)] hover:underline" onClick={() => { setConfirmStop(true); }}>Don’t call this patient again</button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!canSave} onClick={() => { onSave(row); }}>Save</Button>
          </div>
        </div>
      </div>

      {confirmStop ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(20,22,28,0.5)] px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-[440px] rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-modal)]">
            <h3 className="m-0 text-[15px] font-bold text-[var(--text-primary)]">Don’t call {firstName(row.name)} again?</h3>
            <p className="m-0 mt-2 text-[12.5px] leading-[1.6] text-[var(--text-secondary)]">
              Every reason leaves Calls, and no one — ELVA or your team — calls {firstName(row.name)} again until someone turns calls back on in their profile. Texts and emails follow their own consent.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => { setConfirmStop(false); }}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => { setConfirmStop(false); onStopCalls(row); }}>Stop calls</Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function firstName(name: string) { return name.split(' ')[0] ?? name; }

function Step({ n, title, note, className }: { n: string; title: string; note?: string; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-baseline gap-2', className)}>
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-indigo)] text-[11px] font-bold text-white">{n}</span>
      <span className="text-[13.5px] font-bold text-[var(--text-primary)]">{title}</span>
      {note !== undefined ? <span className="text-[11.5px] text-[var(--text-muted)]">{note}</span> : null}
    </div>
  );
}

function InvisFold() {
  const [smile, setSmile] = useState<string[]>([]);
  const [concerns, setConcerns] = useState<string[]>([]);
  const [decides, setDecides] = useState<string | null>(null);
  const [event, setEvent] = useState<string | null>(null);
  const toggle = (list: string[], set: (v: string[]) => void, v: string) => { set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]); };
  const Chip = ({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) => (
    <button type="button" onClick={onClick} className={cn('rounded-full border px-2.5 py-0.5 text-[11px]', on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] font-semibold text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-secondary)]')}>{label}</button>
  );
  return (
    <div className="mt-2.5 border-t border-[var(--border-default)] pt-2.5">
      <FoldRow label="Smile profile">{CALL_INVIS_SMILE.map((s) => <Chip key={s} label={s} on={smile.includes(s)} onClick={() => { toggle(smile, setSmile, s); }} />)}</FoldRow>
      <FoldRow label="Concerns">{CALL_INVIS_CONCERNS.map((s) => <Chip key={s} label={s} on={concerns.includes(s)} onClick={() => { toggle(concerns, setConcerns, s); }} />)}</FoldRow>
      <FoldRow label="Who else decides">{CALL_INVIS_DECIDES.map((s) => <Chip key={s} label={s} on={decides === s} onClick={() => { setDecides(decides === s ? null : s); }} />)}</FoldRow>
      <FoldRow label="Coming up">{CALL_INVIS_EVENTS.map((s) => <Chip key={s} label={s} on={event === s} onClick={() => { setEvent(event === s ? null : s); }} />)}</FoldRow>
    </div>
  );
}
function FoldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-1.5">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">{label}</div>
      <div className="mt-1 flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function whatHappensNext(row: CallRow, reach: string | null, picks: Record<number, string>, by: string): string {
  if (reach === null) return 'Pick an answer above to see what happens.';
  if (reach === 'wrong-number') return `Every reason leaves Calls and the number is flagged on ${firstName(row.name)}’s record. Logged as ${by}.`;
  if (reach === 'no-answer' || reach === 'voicemail') {
    const tries = row.tries.length + 1;
    return `Tried ${String(tries)} of 3. ${firstName(row.name)} is back on the list tomorrow${row.bestTime !== undefined ? ' at their best time' : ''}. A third try with no answer and they leave Calls — the agents carry on by text and email. They come back if they do something new.`;
  }
  // reached — one line per reason, so the person sees exactly what each pick does.
  const lines = row.reasons.map((r, i) => {
    const meta = CALL_REASON_META[r.type];
    const pick = picks[i];
    if (pick === undefined) return `${meta.label}: pick an outcome`;
    if (pick === 'booked') return `${meta.label}: booked 14 Oct, 8:00 am with Dr. Carter — found in the calendar`;
    const label = callOutcomesFor(r.type).find((o) => o.key === pick)?.label ?? pick;
    return `${meta.label}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
  });
  const done = row.reasons.every((_, i) => picks[i] === 'booked' || picks[i] === 'paid' || picks[i] === 'link-texted' || picks[i] === 'not-interested' || picks[i] === 'disputes');
  const tail = done ? `${firstName(row.name)} leaves Calls — every reason is done.` : `Reasons with a wait come back on their own.`;
  return `${lines.join(' · ')}. ${tail} Logged as ${by} on the record.`;
}
